<template>
  <div ref="containerRef">
    <div v-if="!isProduction" v-cloak>
      <callout-v2 v-if="variant === 'v2'" icon intent="info" class="mb-3 flex items-start gap-2 p-4 text-sm text-normal">
        <div class="grid gap-1.5">
          <h3 class="text-display-ui-6 text-strong">Test card</h3>
          <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
            <dt class="text-subtle">Card number</dt>
            <dd class="tabular-nums">4242 4242 4242 4242</dd>
            <dt class="text-subtle">Expiration date</dt>
            <dd class="tabular-nums">04 / 28</dd>
            <dt class="text-subtle">Security code</dt>
            <dd class="tabular-nums">242</dd>
          </dl>
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://stripe.com/docs/testing"
            class="intent-accent text-strong underline underline-offset-2"
            >View testing documentation</a
          >
        </div>
      </callout-v2>
      <div v-else class="info">
        <div
          style="
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          "
        >
          <div>TEST CARD</div>
          <div>4242424242424242</div>
          <div>0428</div>
          <div>242</div>
        </div>
        <div style="display: flex; justify-content: center">
          <small
            ><a target="_blank" rel="noopener noreferrer" href="https://stripe.com/docs/testing"
              >Documentation</a
            ></small
          >
        </div>
      </div>
    </div>

    <div v-if="busy">Loading...</div>
    <div id="card-element" />
    <inline-alert v-if="cardErrors" :message="cardErrors" style="margin-top: 0.5rem"/>
  </div>
</template>
<script>
import { PaymentGatewayGetTokenError } from "../errors";
import { getDefaultStripeOptions } from "../common";
import InlineAlert from "./inline-alert.vue";
import CalloutV2 from "./CalloutV2.vue";

// Stripe's appearance API does not reliably resolve color-mix() inside
// boxShadow values rendered in the cross-origin iframe, so the v2 focus
// halo needs a concrete colour. Roadie's accent token is an oklch() string;
// we wrap it (or a #hex fallback) in a CSS variable-friendly form that
// Stripe will pass through verbatim, then layer the alpha via a separate
// channel using color-mix-equivalent oklch syntax which IS supported.
function hexOrColorToRgba(color, alpha) {
  const trimmed = String(color || '').trim();
  if (!trimmed) return `rgba(1, 145, 235, ${alpha})`;
  if (trimmed.startsWith('oklch')) {
    // oklch(L C H) -> oklch(L C H / <alpha>)
    return trimmed.replace(/\)\s*$/, ` / ${alpha})`);
  }
  if (trimmed.startsWith('#')) {
    const hex = trimmed.slice(1);
    const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // rgb()/hsl() — wrap in color-mix as a best-effort fallback
  return `color-mix(in oklch, ${trimmed} ${alpha * 100}%, transparent)`;
}

export default {
  components: {InlineAlert, 'callout-v2': CalloutV2},
  data() {
    return {
      busy: null,
      stripe: null,
      card: null,
      cardErrors: null,
    };
  },
  methods: {
    initialize() {
      Promise.resolve()
        .then(this.setBusy)
        .then(this.createPaymentElement)
        .catch(this.handleError)
        .then(this.hideBusy);
    },

    setBusy() {
      this.$emit("loading", this.type, true);
      this.busy = true;
    },

    hideBusy() {
      this.$emit("loading", this.type, false);
      this.busy = false;
    },

    handleError(err) {
      console.error(err.message);
      this.onError(err.message);
    },

    createPaymentElement() {
      let stripeOptions = getDefaultStripeOptions();
      if (this.stripeAccount) {
        stripeOptions.stripeAccount = this.stripeAccount;
      }

      const stripe = Stripe(this.clientToken, stripeOptions);

      const styles = getComputedStyle(this.$refs.containerRef ?? document.body);
      // v2 variant uses Checkout v2's design tokens (Tailwind CSS vars) so the
      // Stripe Elements iframe blends with the v2 surface palette. v1 default
      // (omitted prop) preserves the original Open Sans / 4px radius styling.
      let appearance;
      if (this.variant === 'v2') {
        // Stripe Elements is iframed, so its CSS vars don't inherit from the
        // host page. Read the Roadie tokens off the v2 container and forward
        // concrete OKLCH values so the iframe matches InputV2 / SelectV2
        // (emphasis-sunken + Intermission + 8px radius).
        const accent = styles.getPropertyValue('--color-accent-9').trim() || '#0191eb';
        const danger = styles.getPropertyValue('--color-danger-9').trim() || '#dc2626';
        // is-interactive-field paints `aria-invalid` with a subtle danger-2
        // bg tint + danger-9 border. Mirror those in Stripe so the iframe
        // error state reads like InputV2 / SelectV2. The error TEXT, however,
        // needs to match FieldV2's `intent-danger text-subtle` which renders
        // at --color-danger-11. Read by simulating the cascade in a throwaway
        // element so Tailwind v4's @theme inline namespace resolves correctly.
        const dangerBgTint = styles.getPropertyValue('--color-danger-2').trim() || '#fef2f2';
        const probe = document.createElement('span');
        probe.className = 'intent-danger text-subtle';
        probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;';
        (this.$refs.containerRef ?? document.body).appendChild(probe);
        const dangerText = getComputedStyle(probe).color
          || styles.getPropertyValue('--color-danger-11').trim()
          || danger;
        probe.remove();
        const variables = {
          colorText: styles.getPropertyValue('--color-neutral-13').trim() || styles.getPropertyValue('--primary-text-color'),
          colorTextPlaceholder: styles.getPropertyValue('--color-neutral-9').trim(),
          colorDanger: danger,
          // Use the raised surface for all Stripe inputs. Stripe Elements
          // styles its country <select> with the same .Input class as text
          // inputs, so the sunken treatment that matches InputV2 makes the
          // dropdown look wrong. Raised matches SelectV2 and reads
          // consistently across the whole iframe.
          colorBackground: styles.getPropertyValue('--background-color-raised').trim()
            || styles.getPropertyValue('--color-neutral-0').trim()
            || '#ffffff',
          colorPrimary: accent,
          colorBorder: styles.getPropertyValue('--border-color-normal').trim()
            || styles.getPropertyValue('--color-neutral-7').trim(),
          fontFamily: `'Intermission', 'Inter', ui-sans-serif, system-ui, sans-serif`,
          fontSizeBase: '16px',
          fontWeightNormal: '400',
          borderRadius: '8px',
          spacingUnit: '4px',
        };
        appearance = {
          theme: "stripe",
          variables,
          rules: {
            '.Input': {
              // Stripe's iframe sizes inputs off --c-inputHeight, which
              // its appearance API does not expose, and silently drops
              // an explicit `height` on .Input. Tune padding instead so
              // the box totals 40px (h-10): 22px line-height +
              // 8px×2 padding + 1px×2 border = 40px. Matches InputV2
              // and SelectV2 vertical rhythm.
              lineHeight: '22px',
              padding: '8px 8px',
              border: `1px solid ${variables.colorBorder}`,
              boxShadow: 'none',
            },
            '.Input:focus': {
              border: `1px solid ${accent}`,
              // Concrete rgba/oklch halo — Stripe iframe doesn't
              // reliably resolve color-mix() inside boxShadow.
              boxShadow: `0 0 0 3px ${hexOrColorToRgba(accent, 0.25)}`,
              outline: 'none',
            },
            '.Input--invalid': {
              // Match is-interactive-field: danger-2 fill + danger-9
              // border, halo only on focus. Stripe paints its own halo
              // by default; explicit `boxShadow: none` suppresses it.
              backgroundColor: dangerBgTint,
              border: `1px solid ${danger}`,
              boxShadow: 'none',
              color: dangerText,
            },
            '.Input--invalid:focus': {
              backgroundColor: dangerBgTint,
              border: `1px solid ${danger}`,
              boxShadow: `0 0 0 3px ${hexOrColorToRgba(danger, 0.25)}`,
              outline: 'none',
            },
            '.Error': {
              fontSize: '14px',
              fontWeight: '400',
              color: dangerText,
              marginTop: '6px',
            },
            '.Label': {
              fontWeight: '500',
              fontSize: '14px',
              marginBottom: '6px',
            },
          },
        };
      } else {
        appearance = {
          theme: "stripe",
          variables: {
            colorText: styles.getPropertyValue("--primary-text-color"),
            colorDanger: styles.getPropertyValue("--error-color"),
            fontFamily: `'Open Sans', sans-serif`,
            fontWeightNormal: 300,
            colorBackground: "#ffffff",
            borderRadius: '4px',
          },
          rules: {
            '.Input--invalid': {
              boxShadow: '0 0 2px var(--colorDanger)',
            },
          },
        };
      }
      const stripeStyles = {
        appearance,
        fonts: [
          {
            cssSrc: "https://fonts.googleapis.com/css?family=Open+Sans:300,400,400i,500,600&display=swap",
          },
        ]
      }
      
      const options = {
        paymentMethodCreation: "manual",
        currency: this.currency,
        paymentMethodTypes: [
          "card",
          ...(this.enableAfterpay ? ["afterpay_clearpay"] : [])
        ],
        ...this.stripeMode === "setup"
          ? {
            mode: "setup",
          } : {
            mode: "payment",
            amount: this.totalPriceInCents,
          },
        ...stripeStyles
      };
      const elements = stripe.elements(options);
      const paymentElement = elements.create("payment", {
        wallets: {
          applePay: this.enableApplePay ? "auto" : "never",
        },
      });

      paymentElement.mount("#card-element");

      paymentElement.addEventListener("change", (event) => {
        this.cardErrors = event.error ? event.error.message : null;
      });

      this.stripe = stripe;
      this.card = paymentElement;
      this.elements = elements;
    },

    validateCardType(paymentMethod) {
      if (this.allowAmericanExpress) {
        return paymentMethod;
      }

      const creditCardType =
        (paymentMethod && paymentMethod.card && paymentMethod.card.brand) || "";
      const isInvalid = creditCardType == "amex";

      if (isInvalid) {
        throw new PaymentGatewayGetTokenError(
          "American Express Credit Cards are not accepted"
        );
      }

      return paymentMethod;
    },

    getToken() {
      return Promise.resolve()
        .then(() => this.elements.submit())
        .then(({ error }) => {
          if (error) throw new PaymentGatewayGetTokenError(error.message);
        })
        .then(() =>
          this.stripe.createPaymentMethod({
            elements: this.elements,
          })
        )
        .then(({ paymentMethod, error }) => {
          if (error) {
            throw new PaymentGatewayGetTokenError(error.message);
          }
          if (!paymentMethod) {
            throw new Error(
              "Stripe did not return a payment method with successful response."
            );
          }
          return paymentMethod;
        })
        .then(this.validateCardType)
        .then((paymentMethod) => {
          return paymentMethod.id;
        })
        .catch((exception) => {
          this.cardErrors = exception.message;
          throw exception;
        });
    },
  },

  mounted() {
    this.initialize();
  },

  props: {
    clientToken: {
      type: String,
      required: true,
    },
    stripeAccount: {
      type: String,
    },
    stripeMode: {
      type: String,
      default: "payment",
    },
    isProduction: {
      type: Boolean,
      required: true,
    },
    type: {
      type: String,
      required: true,
    },
    onError: {
      type: Function,
      required: true,
    },
    allowAmericanExpress: {
      type: Boolean,
    },
    totalPriceInCents: {
      type: Number,
    },
    currency: {
      type: String,
      required: true,
    },
    enableApplePay: {
      type: Boolean,
      required: true,
    },
    enableAfterpay: {
      type: Boolean,
      required: true,
    },
    // v2-only additive prop — when set to 'v2' the Stripe Elements appearance
    // reads Checkout v2 design tokens. Default 'v1' is byte-identical to the
    // original behaviour, so existing v1 callers are unaffected.
    variant: {
      type: String,
      default: 'v1',
    },
  },
};
</script>
